import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MdCheckCircle, MdErrorOutline } from 'react-icons/md';
import '../components/shared/PanelToast.css';

const PanelToastContext = createContext(null);

function PanelToastItem({ toast }) {
  const isSuccess = toast.type === 'success';

  return (
    <div className={`panel-toast panel-toast-${toast.type}`} role="status" aria-live="polite">
      <span className="panel-toast-icon">
        {isSuccess ? <MdCheckCircle /> : <MdErrorOutline />}
      </span>
      <div className="panel-toast-body">
        <div className="panel-toast-title">{toast.title}</div>
        {toast.message && <div className="panel-toast-message">{toast.message}</div>}
      </div>
    </div>
  );
}

export function PanelToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const lastErrorRef = useRef({ key: '', at: 0 });

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const pushToast = useCallback(({ title, message = '', type = 'success', duration = 3200 }) => {
    // Prevent identical error toasts from stacking (Strict Mode / double handlers)
    if (type === 'error') {
      const key = `${title}::${message}`;
      const now = Date.now();
      if (lastErrorRef.current.key === key && now - lastErrorRef.current.at < 2500) {
        return null;
      }
      lastErrorRef.current = { key, at: now };
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => [...prev.slice(-4), { id, title, message, type }]);
    window.setTimeout(() => dismissToast(id), duration);
    return id;
  }, [dismissToast]);

  const api = useMemo(() => ({
    showSuccess: (message, title = 'Success') => pushToast({ title, message, type: 'success' }),
    showError: (message, title = 'Something went wrong') => pushToast({
      title,
      message: String(message || '').trim(),
      type: 'error',
      duration: Math.min(12000, 4200 + Math.max(0, String(message || '').length * 25)),
    }),
    showAdded: (label) => pushToast({ title: 'Added successfully', message: `${label} has been added.`, type: 'success' }),
    showUpdated: (label) => pushToast({ title: 'Updated successfully', message: `${label} has been updated.`, type: 'success' }),
    showDeleted: (label) => pushToast({ title: 'Deleted successfully', message: `${label} has been deleted.`, type: 'success' }),
    showSaved: (label = 'Changes') => pushToast({ title: 'Saved successfully', message: `${label} saved.`, type: 'success' }),
  }), [pushToast]);

  const stack = (
    <div className="panel-toast-stack" aria-live="polite" aria-relevant="additions">
      {toasts.map((toast) => (
        <PanelToastItem key={toast.id} toast={toast} />
      ))}
    </div>
  );

  return (
    <PanelToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' ? createPortal(stack, document.body) : stack}
    </PanelToastContext.Provider>
  );
}

export function usePanelToast() {
  const context = useContext(PanelToastContext);
  if (!context) {
    throw new Error('usePanelToast must be used within PanelToastProvider');
  }
  return context;
}
